import jsPDF from "jspdf"
import autoTable, { type CellHookData } from "jspdf-autotable"
import type { TFunction } from "i18next"

import type {
  CouplingLoadView,
  UsinageDetail,
} from "@/features/milling/api/types"

import {
  type CouplageReportData,
  buildCouplageReport,
  reportSourceKey,
} from "./couplage-report"
import {
  PDF_BORDER,
  PDF_SECTION_BAND,
  PDF_TEXT_MUTED,
  PDF_TEXT_PRIMARY,
  PDF_TEXT_SECONDARY,
  PDF_TOTAL_BAND,
  PDF_TYPE,
  formatPdfNumber,
  pdfHeadStyles,
  pdfTableStyles,
} from "./pdf-shared"
import { type RapportData, buildRapport } from "./rapport"

// downloadUsinageRapportPdf renders the rapport synthèse — Entrée
// Brousse + Fabrication side-by-side at the top, the chargement ×
// planned-lot couplage matrix in the middle, and the per-lot Freinte
// reconciliation at the bottom. Landscape A4 to fit the matrix's
// dynamic column count (one per Lot Planned).
//
// Each major section is rendered top-to-bottom with an explicit
// space-check before drawing; if the section won't fit on the
// current page, we addPage() first. This prevents the
// "section-duplicated-on-pages-1-and-2" symptom that happens when
// individual autoTable calls page-break independently of each other
// and the imperative title/meta only exists on page 1.
export function downloadUsinageRapportPdf(
  usinage: UsinageDetail,
  loads: CouplingLoadView[],
  t: TFunction<"milling">,
) {
  const rapport = buildRapport(usinage, loads)
  const couplage = buildCouplageReport(usinage, loads)
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" })
  drawDocument(doc, rapport, couplage, t)
  drawFooters(doc, t)
  doc.save(`${rapport.usinageNumber}-usinage.pdf`)
}

const MARGIN = 36
const HEADER_BLOCK_HEIGHT = 48 // title + meta line
const FOOTER_RESERVE = 28
const SECTION_GAP = 18
const SECTION_HEADER_HEIGHT = 20

function drawDocument(
  doc: jsPDF,
  rapport: RapportData,
  couplage: CouplageReportData,
  t: TFunction<"milling">,
) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const usable = pageWidth - MARGIN * 2

  drawHeader(doc, rapport, t)
  let y = MARGIN + HEADER_BLOCK_HEIGHT

  // 1) Side-by-side Entrée Brousse + Fabrication.
  //
  // Both tables are bounded — Entrée has one row per source
  // assignment, Fabrication one row per produced lot — so the
  // conservative pre-estimate of "headers + rows × line-height" is
  // accurate enough to decide pagination without rendering twice.
  const gutter = 14
  const halfWidth = (usable - gutter) / 2
  const twoUpEstimate = Math.max(
    estimateTableHeight(rapport.entreeRows.length, /*foot rows*/ 1),
    estimateTableHeight(rapport.fabricationRows.length, /*foot rows*/ 1),
  ) + SECTION_HEADER_HEIGHT
  y = ensureSpace(doc, rapport, t, y, twoUpEstimate)

  drawSectionHeader(doc, t("rapport.entreeBrousse"), MARGIN, y, halfWidth)
  drawEntreeTable(doc, rapport, t, MARGIN, y + SECTION_HEADER_HEIGHT, halfWidth)
  const leftFinalY = finalY(doc)

  const rightX = MARGIN + halfWidth + gutter
  drawSectionHeader(doc, t("rapport.fabrication"), rightX, y, halfWidth)
  drawFabricationTable(
    doc,
    rapport,
    t,
    rightX,
    y + SECTION_HEADER_HEIGHT,
    halfWidth,
  )
  const rightFinalY = finalY(doc)

  y = Math.max(leftFinalY, rightFinalY) + SECTION_GAP

  // 2) Couplage matrix.
  //
  // The matrix can grow tall (one body row per source) and is the
  // most likely section to spill onto a second page. autoTable
  // handles the actual spill, but we need to re-draw the rapport
  // header on the spilled page so a reader landing on page 2 sees
  // the title + operator instead of an orphan continuation. The
  // `didDrawPage` callback wraps that re-draw and dodges the
  // initial-page firing via a startPage check.
  const couplageEstimate =
    SECTION_HEADER_HEIGHT +
    estimateTableHeight(couplage.sources.length, /*foot rows*/ 1) +
    /*two-row head*/ 14
  y = ensureSpace(doc, rapport, t, y, couplageEstimate)
  drawSectionHeader(doc, t("couplageReport.title"), MARGIN, y, usable)
  const couplageStartPage = doc.getNumberOfPages()
  drawCouplageMatrix(doc, couplage, t, MARGIN, y + SECTION_HEADER_HEIGHT, () =>
    onAutoTablePage(doc, rapport, t, couplageStartPage),
  )
  y = finalY(doc) + SECTION_GAP

  // 3) Freinte reconciliation (table + 5 summary rows in the foot).
  const freinteEstimate =
    SECTION_HEADER_HEIGHT +
    estimateTableHeight(rapport.freinte.rows.length, /*foot rows*/ 5)
  y = ensureSpace(doc, rapport, t, y, freinteEstimate)
  drawSectionHeader(doc, t("rapport.freinteHeading"), MARGIN, y, usable)
  const freinteStartPage = doc.getNumberOfPages()
  drawFreinteTable(
    doc,
    rapport,
    t,
    MARGIN,
    y + SECTION_HEADER_HEIGHT,
    usable,
    () => onAutoTablePage(doc, rapport, t, freinteStartPage),
  )
}

// onAutoTablePage is the didDrawPage callback used by sections we
// allow to spill. It runs after autoTable has painted the new page;
// when the current page is past `startPage`, we know autoTable
// created a continuation and we re-paint the rapport header. We
// skip the re-paint for the start page since the caller already
// painted the surrounding chrome there.
function onAutoTablePage(
  doc: jsPDF,
  rapport: RapportData,
  t: TFunction<"milling">,
  startPage: number,
) {
  if (doc.getNumberOfPages() <= startPage) return
  drawHeader(doc, rapport, t)
}

// drawHeader paints the title + operator line at the top of the
// current page. Called once per page so every page reads as a
// rapport page rather than an orphan continuation.
function drawHeader(
  doc: jsPDF,
  rapport: RapportData,
  t: TFunction<"milling">,
) {
  const pageWidth = doc.internal.pageSize.getWidth()
  doc.setFont("helvetica", PDF_TYPE.title.weight)
  doc.setFontSize(PDF_TYPE.title.size)
  doc.setTextColor(PDF_TEXT_PRIMARY)
  doc.text(
    t("rapport.pdfTitle", {
      usinageNumber: rapport.usinageNumber,
      from: rapport.dateRange.from,
      to: rapport.dateRange.to,
      defaultValue: `USINAGE N° {{usinageNumber}} DU {{from}} au {{to}}`,
    }),
    pageWidth / 2,
    MARGIN + 14,
    { align: "center" },
  )

  doc.setFont("helvetica", PDF_TYPE.meta.weight)
  doc.setFontSize(PDF_TYPE.meta.size)
  doc.setTextColor(PDF_TEXT_SECONDARY)
  doc.text(
    `${t("rapport.operator")}: ${rapport.operator}`,
    MARGIN,
    MARGIN + 34,
  )
  doc.setTextColor(PDF_TEXT_PRIMARY)
}

// ensureSpace checks whether `needed` points still fit before the
// page's footer reserve; if not, it adds a fresh page (with header)
// and returns the new starting y. Used between major sections so a
// section never starts within `FOOTER_RESERVE` of the page bottom.
function ensureSpace(
  doc: jsPDF,
  rapport: RapportData,
  t: TFunction<"milling">,
  y: number,
  needed: number,
): number {
  const pageHeight = doc.internal.pageSize.getHeight()
  if (y + needed <= pageHeight - FOOTER_RESERVE) return y
  doc.addPage()
  drawHeader(doc, rapport, t)
  return MARGIN + HEADER_BLOCK_HEIGHT
}

// estimateTableHeight is an upper-bound estimate for autoTable
// output: one header row + N body rows + M foot rows at the shared
// row height, plus a small buffer. The font defaults to 9pt in
// pdfTableStyles() so 18pt per row covers the cell padding.
function estimateTableHeight(bodyRows: number, footRows: number): number {
  const rowHeight = 18
  const headerHeight = 22
  return headerHeight + (bodyRows + footRows) * rowHeight + 6
}

// drawCouplageMatrix renders the chargement × planned-lot grid: two
// header tiers, per-source rows, TOTAL SACS footer. Mirrors the
// dedicated couplage rapport PDF so both documents read the same.
function drawCouplageMatrix(
  doc: jsPDF,
  couplage: CouplageReportData,
  t: TFunction<"milling">,
  margin: number,
  y: number,
  onDrawPage?: () => void,
) {
  const leftColCount = 5

  const headTop = [
    {
      content: t("couplageReport.leftHeader"),
      colSpan: leftColCount,
      styles: {
        halign: "center" as const,
        fillColor: PDF_SECTION_BAND,
        fontStyle: "bold" as const,
      },
    },
    {
      content: t("couplageReport.lotsHeader"),
      colSpan: couplage.lots.length + 1,
      styles: {
        halign: "center" as const,
        fillColor: PDF_SECTION_BAND,
        fontStyle: "bold" as const,
      },
    },
  ]

  const headBottom = [
    t("rapport.cols.fournisseur"),
    t("rapport.cols.cccNumber"),
    t("couplageReport.cols.nbreSacs"),
    t("couplageReport.cols.poidsNet"),
    t("couplageReport.cols.poidsMoyenne"),
    ...couplage.lots.map((l) => l.label),
    t("couplageReport.cols.reste"),
  ]

  const body = couplage.sources.map((s) => {
    const key = reportSourceKey(s.kind, s.id)
    const left: (string | number)[] = [
      s.fournisseur,
      s.cccNumber,
      formatPdfNumber(s.nbreSacs),
      formatPdfNumber(s.poidsNetKg, 2),
      formatPdfNumber(s.poidsMoyenneKg, 2),
    ]
    const matrix = couplage.lots.map((l) => {
      const bags = l.bagsBySource[key] ?? 0
      return bags > 0 ? formatPdfNumber(bags) : ""
    })
    const reste = couplage.resteBySource[key] ?? 0
    return [...left, ...matrix, reste > 0 ? formatPdfNumber(reste) : ""]
  })

  // TOTAL SACS footer row — planning-only, no fabricated weights
  // (this rapport's actual per-lot weights live in the Freinte
  // section below).
  const totalRow = [
    {
      content: t("couplageReport.totalSacs"),
      colSpan: 2,
      styles: {
        halign: "left" as const,
        fillColor: PDF_TOTAL_BAND,
        fontStyle: "bold" as const,
      },
    },
    // Bag totals are integers; weight totals get 2 decimals to match
    // the per-row precision.
    {
      content: formatPdfNumber(couplage.totalNbreSacs),
      styles: {
        halign: "right" as const,
        fillColor: PDF_TOTAL_BAND,
        fontStyle: "bold" as const,
      },
    },
    {
      content: formatPdfNumber(couplage.totalPoidsNetKg, 2),
      styles: {
        halign: "right" as const,
        fillColor: PDF_TOTAL_BAND,
        fontStyle: "bold" as const,
      },
    },
    {
      content: formatPdfNumber(couplage.totalPoidsMoyenneKg, 2),
      styles: {
        halign: "right" as const,
        fillColor: PDF_TOTAL_BAND,
        fontStyle: "bold" as const,
      },
    },
    ...couplage.lots.map((l) => ({
      content: formatPdfNumber(couplage.totalBagsByLot[l.id] ?? 0),
      styles: {
        halign: "right" as const,
        fillColor: PDF_TOTAL_BAND,
        fontStyle: "bold" as const,
      },
    })),
    {
      content: formatPdfNumber(couplage.totalResteSacs),
      styles: {
        halign: "right" as const,
        fillColor: PDF_TOTAL_BAND,
        fontStyle: "bold" as const,
      },
    },
  ]

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [headTop, headBottom],
    body,
    foot: [totalRow],
    styles: pdfTableStyles(),
    headStyles: pdfHeadStyles(),
    columnStyles: {
      2: { halign: "right" as const },
      3: { halign: "right" as const },
      4: { halign: "right" as const },
      ...Object.fromEntries(
        couplage.lots.map((_, i) => [
          5 + i,
          { halign: "right" as const },
        ]),
      ),
      [5 + couplage.lots.length]: { halign: "right" as const },
    },
    didParseCell: (cell: CellHookData) => {
      if (
        cell.section === "body" &&
        (cell.column.index === 0 || cell.column.index === 1)
      ) {
        cell.cell.styles.halign = "left"
      }
    },
    didDrawPage: () => {
      // If autoTable spills onto a new page, the caller can re-draw
      // the rapport header so the continuation reads as part of the
      // same document rather than a blank fragment.
      onDrawPage?.()
    },
  })
}

function drawSectionHeader(
  doc: jsPDF,
  label: string,
  x: number,
  y: number,
  width: number,
) {
  const h = 18
  doc.setFillColor(...PDF_SECTION_BAND)
  doc.setDrawColor(...PDF_BORDER)
  doc.setLineWidth(0.5)
  doc.rect(x, y, width, h, "FD")
  doc.setFont("helvetica", PDF_TYPE.sectionHeader.weight)
  doc.setFontSize(PDF_TYPE.sectionHeader.size)
  doc.setTextColor(PDF_TEXT_PRIMARY)
  doc.text(label, x + 8, y + 12)
}

function drawEntreeTable(
  doc: jsPDF,
  rapport: RapportData,
  t: TFunction<"milling">,
  x: number,
  y: number,
  width: number,
) {
  const totalPoids = rapport.entreeRows.reduce((a, r) => a + r.poidsKg, 0)
  const totalRefract = rapport.entreeRows.reduce(
    (a, r) => a + r.refractKg,
    0,
  )

  autoTable(doc, {
    startY: y,
    margin: { left: x, right: doc.internal.pageSize.getWidth() - x - width },
    tableWidth: width,
    // Side-by-side block — the caller pre-reserved space via
    // ensureSpace(), so we forbid autoTable from page-breaking
    // mid-table. A break here would orphan the right-hand sibling
    // and reproduce the duplicate-content-on-page-2 symptom.
    pageBreak: "avoid",
    head: [
      [
        t("rapport.cols.fournisseur"),
        t("rapport.cols.cccNumber"),
        t("rapport.cols.poids"),
        t("rapport.cols.refract"),
      ],
    ],
    body: rapport.entreeRows.map((r) => [
      r.fournisseur,
      r.cccNumber,
      formatPdfNumber(r.poidsKg, 2),
      r.refractKg > 0 ? formatPdfNumber(r.refractKg, 2) : "",
    ]),
    foot: [
      [
        {
          content: t("rapport.total"),
          colSpan: 2,
          styles: {
            halign: "left" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
        {
          content: formatPdfNumber(totalPoids, 2),
          styles: {
            halign: "right" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
        {
          content: formatPdfNumber(totalRefract, 2),
          styles: {
            halign: "right" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
      ],
    ],
    styles: pdfTableStyles(),
    headStyles: pdfHeadStyles(),
    columnStyles: {
      2: { halign: "right" as const },
      3: { halign: "right" as const },
    },
  })
}

function drawFabricationTable(
  doc: jsPDF,
  rapport: RapportData,
  t: TFunction<"milling">,
  x: number,
  y: number,
  width: number,
) {
  const totalPoids = rapport.fabricationRows.reduce(
    (a, r) => a + r.poidsKg,
    0,
  )

  autoTable(doc, {
    startY: y,
    margin: { left: x, right: doc.internal.pageSize.getWidth() - x - width },
    tableWidth: width,
    pageBreak: "avoid",
    head: [
      [
        t("rapport.cols.numeroLot"),
        t("rapport.cols.poids"),
        t("rapport.cols.typeProduit"),
      ],
    ],
    body: rapport.fabricationRows.map((r) => [
      r.numeroLot,
      formatPdfNumber(r.poidsKg, 2),
      r.typeProduit,
    ]),
    foot: [
      [
        {
          content: t("rapport.total"),
          styles: {
            halign: "left" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
        {
          content: formatPdfNumber(totalPoids, 2),
          styles: {
            halign: "right" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
        {
          content: "",
          styles: { fillColor: PDF_TOTAL_BAND },
        },
      ],
    ],
    styles: pdfTableStyles(),
    headStyles: pdfHeadStyles(),
    columnStyles: { 1: { halign: "right" as const } },
  })
}

function drawFreinteTable(
  doc: jsPDF,
  rapport: RapportData,
  t: TFunction<"milling">,
  x: number,
  y: number,
  width: number,
  onDrawPage?: () => void,
) {
  const { rows, summary } = rapport.freinte
  // Per-planned-lot reconciliation table: one row per Lot Planned
  // with planned weight, actual weight, ecart. Pending entries show
  // dashes so the table is readable mid-fabrication too.
  autoTable(doc, {
    startY: y,
    margin: { left: x, right: doc.internal.pageSize.getWidth() - x - width },
    tableWidth: width,
    head: [
      [
        t("rapport.freinteCols.plannedLot"),
        t("rapport.freinteCols.lotNumber"),
        t("rapport.freinteCols.plannedKg"),
        t("rapport.freinteCols.actualKg"),
        t("rapport.freinteCols.ecartKg"),
      ],
    ],
    body: rows.map((r) => [
      r.plannedLabel,
      r.fabricatedLotNumber ?? t("rapport.freintePending"),
      formatPdfNumber(r.plannedKg, 2),
      r.actualKg != null
        ? formatPdfNumber(r.actualKg, 2)
        : t("rapport.freintePending"),
      r.ecartKg != null
        ? formatPdfNumber(r.ecartKg, 2)
        : t("rapport.freintePending"),
    ]),
    foot: [
      [
        {
          content: t("rapport.freinteSummary.totalPlanned"),
          colSpan: 2,
          styles: {
            halign: "left" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
        {
          content: `${formatPdfNumber(summary.totalPlannedKg, 2)} kg`,
          colSpan: 3,
          styles: {
            halign: "right" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
      ],
      [
        {
          content: t("rapport.freinteSummary.totalActual"),
          colSpan: 2,
          styles: {
            halign: "left" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
        {
          content: `${formatPdfNumber(summary.totalActualKg, 2)} kg`,
          colSpan: 3,
          styles: {
            halign: "right" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
      ],
      [
        {
          content: t("rapport.freinteSummary.waste"),
          colSpan: 2,
          styles: {
            halign: "left" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
        {
          content: `${formatPdfNumber(summary.wasteKg, 2)} kg`,
          colSpan: 3,
          styles: {
            halign: "right" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
      ],
      [
        {
          content: t("rapport.freinteSummary.ecart"),
          colSpan: 2,
          styles: {
            halign: "left" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
        {
          content: `${formatPdfNumber(summary.ecartKg, 2)} kg`,
          colSpan: 3,
          styles: {
            halign: "right" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
      ],
      [
        {
          content: t("rapport.freinteSummary.freinte"),
          colSpan: 2,
          styles: {
            halign: "left" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
        {
          content: `${formatPdfNumber(summary.freintePct, 2)} %`,
          colSpan: 3,
          styles: {
            halign: "right" as const,
            fillColor: PDF_TOTAL_BAND,
            fontStyle: "bold" as const,
          },
        },
      ],
    ],
    styles: pdfTableStyles(),
    headStyles: pdfHeadStyles(),
    columnStyles: {
      2: { halign: "right" as const },
      3: { halign: "right" as const },
      4: { halign: "right" as const },
    },
    didDrawPage: () => {
      // If autoTable spills onto a new page, the caller can re-draw
      // the rapport header so the continuation reads as part of the
      // same document.
      onDrawPage?.()
    },
  })
}

// drawFooters writes the "Page X / Y" line at the bottom of every
// page. Called once after the whole document is laid out so the
// total page count is known. Replaces the earlier single-page
// drawFooter which always reported "Page 1 / 1" regardless of how
// many pages the rapport had grown to.
function drawFooters(doc: jsPDF, t: TFunction<"milling">) {
  const total = doc.getNumberOfPages()
  const pageHeight = doc.internal.pageSize.getHeight()
  const pageWidth = doc.internal.pageSize.getWidth()
  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i)
    doc.setFont("helvetica", PDF_TYPE.footer.weight)
    doc.setFontSize(PDF_TYPE.footer.size)
    doc.setTextColor(PDF_TEXT_MUTED)
    doc.text(
      t("rapport.pageFooter", { page: i, total }),
      pageWidth / 2,
      pageHeight - 18,
      { align: "center" },
    )
    doc.setTextColor(PDF_TEXT_PRIMARY)
  }
}

function finalY(doc: jsPDF): number {
  return (doc as unknown as { lastAutoTable: { finalY: number } })
    .lastAutoTable.finalY
}
